// @flow
import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslationNs } from '@helpers/helpers';
import AppointmentService from '../../../services/AppointmentService';
import DayView from './DayView';
import WeekView from './WeekView';
import MonthView from './MonthView';
import './AndroidAgenda.scss';

type Props = {
  activeModal: ?string,
  appointments: Object[],
  selectedDate: Object,
  switchMode: string,
  clinic: Object,
  clinicDoctors: Object,
  filter: Object,
  loadingItems: Object,
  isFinatialEnabled: boolean,
  user: Object,
  isLoading: boolean,
  isRangeLoaded: boolean,
  rangeFailed: boolean,
  workHours: Object,
  language: string,
  onShowAppointment: Function,
  onNewAppointmentClick: Function,
  onGetAppointments: Function,
  onSelectDay: Function,
  onChangeMode: Function,
};

const AndroidAgenda = ({
  activeModal, appointments, selectedDate, switchMode, clinic, clinicDoctors,
  filter: agendaFilter, loadingItems, isFinatialEnabled, user,
  isLoading, isRangeLoaded, rangeFailed, workHours, language,
  onShowAppointment, onNewAppointmentClick,
  onGetAppointments, onSelectDay, onChangeMode,
}: Props) => {
  const [_] = useTranslationNs('agenda');
  const lastFetchKey = useRef(null);
  const fetchKey = `${switchMode}-${selectedDate.format('YYYY-MM-DD')}`;

  useEffect(() => {
    if (fetchKey !== lastFetchKey.current) {
      lastFetchKey.current = fetchKey;
      onGetAppointments(AppointmentService.singleDayParam(switchMode));
    }
  }, [fetchKey, switchMode, onGetAppointments]);

  // Until its own request comes back, an empty day may still have appointments. A day or week
  // just switched to renders once before its request goes out, so that render counts as loading too.
  const pending = (isLoading || lastFetchKey.current !== fetchKey) && !isRangeLoaded;

  const sharedProps = useMemo(() => ({
    appointments,
    selectedDate,
    clinic,
    clinicDoctors,
    filter: agendaFilter,
    loadingItems,
    isFinatialEnabled,
    user,
    isLoading,
    pending,
    failed: rangeFailed,
    onShowAppointment,
    _,
  }), [
    appointments, selectedDate, clinic, clinicDoctors, agendaFilter,
    loadingItems, isFinatialEnabled, user, isLoading, pending, rangeFailed, onShowAppointment, _,
  ]);

  const handleModeChange = useCallback((mode) => {
    onChangeMode(mode);
  }, [onChangeMode]);

  const isDayMode = switchMode === 'day' || !switchMode;

  const renderView = () => {
    switch (switchMode) {
      case 'week':
        return (
          <WeekView
            {...sharedProps}
            onSelectDay={onSelectDay}
          />
        );
      case 'month':
        return (
          <MonthView
            {...sharedProps}
            onSelectDay={onSelectDay}
          />
        );
      case 'day':
      default:
        return (
          <DayView
            {...sharedProps}
            activeModal={activeModal}
            workHours={workHours}
            language={language}
            onNewAppointmentClick={onNewAppointmentClick}
          />
        );
    }
  };

  return (
    <div className="android-agenda">
      <div className="android-agenda-tabs">
        <button
          className={'agenda-tab' + (switchMode === 'day' ? ' active' : '')}
          onClick={() => handleModeChange('day')}
        >
          {_('label/calendar_day')}
        </button>
        <button
          className={'agenda-tab' + (switchMode === 'week' ? ' active' : '')}
          onClick={() => handleModeChange('week')}
        >
          {_('label/calendar_week')}
        </button>
        <button
          className={'agenda-tab' + (switchMode === 'month' ? ' active' : '')}
          onClick={() => handleModeChange('month')}
        >
          {_('label/calendar_month')}
        </button>
      </div>
      <div className={'android-agenda-body' + (isDayMode ? ' android-agenda-body--day' : '')}>
        {rangeFailed && !isDayMode && <div className="agenda-load-failed" role="alert">{_('label/agenda_load_failed')}</div>}
        {renderView()}
      </div>
    </div>
  );
};

// $FlowFixMe
export default React.memo(AndroidAgenda);
